import { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { useTheme } from '../hooks/useTheme';
import { useMobile } from '../hooks/useWindowWidth';
import { usePageTitle } from '../hooks/usePageTitle';

// ─── Operator-supplied legal details ─────────────────────────────────────────
// The only values in this file that are NOT derivable from the code. Confirmed
// by the operator 2026-08-31; do not edit without him. A privacy policy without
// a controller identity and a working contact address is rejected by both store
// reviewers and is not a valid GDPR Art. 13 notice.
//
// `entity` is the data controller — the registered company, not the product
// name. KaziAtlas is the product; OnePlus Global Inc. is who is accountable for
// the data. `jurisdiction` governs which privacy regime applies, so §7 (Your
// rights) has to stay consistent with it: California means CCPA/CPRA, which is
// why §2 states plainly that we do not sell personal information.
//
// PrivacyPolicyPage.test.tsx pins these against placeholder text — the previous
// value here was a literal 'TODO' string that would have shipped to Apple and
// Google as the registered jurisdiction.
const LEGAL = {
    // The product. Kept separate from `entity`: until now both were the string
    // 'KaziAtlas', so the two readings were indistinguishable and the lede below
    // rendered the controller's name where it means the product.
    product: 'KaziAtlas',
    entity: 'OnePlus Global Inc.',
    contactEmail: 'privacy@kaziatlas.com',
    jurisdiction: 'California, USA',
    // Updated for the public-site analytics disclosure.
    effectiveDate: '9 October 2026',
};

// Shared tokens use the public light palette provided by PublicLayout.
const INK = 'var(--ink)';
const BODY = 'var(--ink-body)';
const MUTED = 'var(--ink-muted)';
const ACCENT_TEXT = 'var(--accent-text)';
const BORDER = '1px solid var(--border)';

const s: Record<string, CSSProperties> = {
    page: { background: 'var(--bg)', color: BODY, fontFamily: 'var(--font-body)', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
    nav: { borderBottom: BORDER, background: 'var(--surface)' },
    navInner: { maxWidth: 820, margin: '0 auto', width: '100%', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem' },
    navHome: { background: 'none', border: 'none', color: MUTED, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', padding: 0 },
    main: { maxWidth: 820, margin: '0 auto', width: '100%', padding: '3rem 1.5rem 5rem', flex: 1, overflowWrap: 'anywhere' },
    h1: { fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.02em', color: INK, margin: '0 0 0.5rem' },
    effective: { color: MUTED, fontSize: '0.875rem', margin: '0 0 2.5rem' },
    lede: { fontSize: '1.05rem', lineHeight: 1.75, margin: '0 0 2.5rem' },
    h2: { fontSize: '1.35rem', fontWeight: 800, color: INK, margin: '2.5rem 0 0.75rem', letterSpacing: '-0.01em' },
    h3: { fontSize: '1rem', fontWeight: 700, color: INK, margin: '1.5rem 0 0.5rem' },
    p: { lineHeight: 1.75, margin: '0 0 1rem' },
    ul: { lineHeight: 1.75, margin: '0 0 1rem', paddingLeft: '1.25rem' },
    tableWrap: { overflowX: 'auto', margin: '0 0 1rem' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: 520 },
    th: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: '2px solid var(--border)', color: INK, fontWeight: 700, whiteSpace: 'nowrap' },
    td: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: BORDER, verticalAlign: 'top' },
    callout: { border: BORDER, borderLeft: '3px solid var(--accent)', borderRadius: 8, background: 'var(--surface)', padding: '1rem 1.25rem', margin: '0 0 1rem' },
    footer: { borderTop: BORDER, padding: '2rem 1.5rem', textAlign: 'center', color: MUTED, fontSize: '0.8rem' },
    link: { color: ACCENT_TEXT, textDecoration: 'none', fontWeight: 600 },
};

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
    return (
        <section id={id}>
            <h2 style={s.h2}>{title}</h2>
            {children}
        </section>
    );
}

export function PrivacyPolicyPage() {
    usePageTitle('Privacy Policy');
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isMobile = useMobile();

    return (
        <div style={s.page}>
            <nav style={s.nav}>
                <div style={{ ...s.navInner, padding: isMobile ? '1rem' : '1rem 1.5rem' }}>
                    <KaziAtlasLogo size={28} onLight={theme === 'light'} />
                    <button style={s.navHome} onClick={() => navigate('/')}>← Back to home</button>
                </div>
            </nav>

            <main style={s.main}>
                <h1 style={s.h1}>Privacy Policy</h1>
                <p style={s.effective}>Effective {LEGAL.effectiveDate}</p>

                <p style={s.lede}>
                    {LEGAL.product} is a project-management platform operated by {LEGAL.entity},
                    registered in {LEGAL.jurisdiction}, which is the data controller for the
                    personal data described here. This policy explains what we collect, why we
                    collect it, who we share it with, and what you can do about it. It covers
                    the web application, the iOS app, and the Android app.
                </p>

                <Section id="collect" title="1. Information we collect">
                    <h3 style={s.h3}>Account information</h3>
                    <p style={s.p}>
                        When you create an account we store your email address, your first and
                        last name, and a cryptographic hash of your password. We never store
                        your password in a readable form.
                    </p>

                    <h3 style={s.h3}>Content you create</h3>
                    <p style={s.p}>
                        We store the content you put into the product: projects, tasks,
                        comments, tags, plans and milestones, time entries, file attachments,
                        and your profile picture. This content belongs to you and to the
                        organisation ("tenant") your account belongs to.
                    </p>

                    <h3 style={s.h3}>Optional integrations</h3>
                    <p style={s.p}>
                        These are off by default. They only apply if you explicitly connect them.
                    </p>
                    <ul style={s.ul}>
                        <li>
                            <strong>Calendar (Google Calendar or iCloud).</strong> If you connect a
                            calendar, we cache a rolling window of your events — from one day ago
                            to seven days ahead — so the assistant can reason about your
                            availability. We store <strong>event titles and times only</strong>. We
                            do not store attendees, locations, or event descriptions, and those
                            fields are never sent to the AI model or written to logs.
                        </li>
                        <li>
                            <strong>Notification channels.</strong> If you enable assistant
                            briefings over email or Telegram, we store that delivery address
                            encrypted (AES-GCM, cryptographically bound to your user account), plus
                            a masked form such as <code>j•••@example.com</code> for display in the
                            UI. The raw address is never returned by our API.
                        </li>
                    </ul>

                    <h3 style={s.h3}>Technical data</h3>
                    <p style={s.p}>
                        We collect server logs (including IP address and request metadata) and,
                        if a crash occurs, a diagnostic crash report. Crash reporting on mobile
                        is only active when a reporting key is configured for that build.
                    </p>
                    <h3 style={s.h3}>Website analytics</h3>
                    <p style={s.p}>
                        We use PostHog in cookieless mode to measure visits to our public website,
                        referral domains, campaign tags, browser and device information, and selected
                        actions such as starting signup, requesting a verification email, completing
                        workspace creation, and clicking the App Store link. PostHog receives network
                        information, including your IP address, to calculate daily visitor estimates.
                        This integration does not create analytics cookies or store an analytics
                        identifier in your browser. We do not send form contents, email addresses,
                        passwords, verification tokens, or private workspace content to PostHog,
                        and do not enable PostHog session recording. We honor browser Do Not Track
                        and Global Privacy Control signals for this analytics collection.
                    </p>
                </Section>

                <Section id="use" title="2. How we use your information">
                    <ul style={s.ul}>
                        <li>To provide the product — authenticate you, store your work, and sync it across web and mobile.</li>
                        <li>To send transactional email you have asked for: invitations, password resets, email-change confirmations, and digests.</li>
                        <li>To operate optional AI features you invoke (see section 4).</li>
                        <li>To keep the service secure and debug failures.</li>
                        <li>To bill you, if you are on a paid plan.</li>
                    </ul>
                    <p style={s.p}>
                        We do not sell your personal information. We do not use your content to
                        train our own models, and we do not serve third-party advertising.
                    </p>
                </Section>

                <Section id="sharing" title="3. Who we share it with">
                    <p style={s.p}>
                        We share data only with the service providers needed to run the product.
                        Each receives the minimum required for its function.
                    </p>
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Provider</th>
                                    <th style={s.th}>Purpose</th>
                                    <th style={s.th}>What it receives</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={s.td}>Amazon Web Services</td>
                                    <td style={s.td}>Hosting, database, file storage</td>
                                    <td style={s.td}>All stored data, at rest in the US (us-east-1)</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Anthropic</td>
                                    <td style={s.td}>AI features you invoke</td>
                                    <td style={s.td}>Only the content in the request (see section 4)</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Sentry</td>
                                    <td style={s.td}>Crash and error reporting</td>
                                    <td style={s.td}>Diagnostic data, stack traces, user identifier</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>PostHog</td>
                                    <td style={s.td}>Public website traffic and signup measurement</td>
                                    <td style={s.td}>Public page paths, referral domains, campaign tags, device information, selected events, and network information used for cookieless visitor estimates</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Stripe</td>
                                    <td style={s.td}>Payment processing</td>
                                    <td style={s.td}>Billing details. We never see your full card number.</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Google</td>
                                    <td style={s.td}>Calendar integration (read-only)</td>
                                    <td style={s.td}>Only if you connect it; OAuth token exchange</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Telegram</td>
                                    <td style={s.td}>Assistant message delivery</td>
                                    <td style={s.td}>Only if you enable it; the briefing content</td>
                                </tr>
                                <tr>
                                    <td style={s.td}>Email provider</td>
                                    <td style={s.td}>Transactional email</td>
                                    <td style={s.td}>Your email address and the message body</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p style={s.p}>
                        We may also disclose information where legally required, or to protect
                        the rights and safety of our users.
                    </p>
                </Section>

                <Section id="ai" title="4. AI processing">
                    <p style={s.p}>
                        Some features — plan generation, summaries, and the proactive assistant —
                        send content to Anthropic's API to produce a response. This only happens
                        when you invoke such a feature or enable the assistant.
                    </p>
                    <div style={s.callout}>
                        <p style={{ ...s.p, margin: 0 }}>
                            You can supply your own Anthropic API key in the admin settings. When
                            you do, AI requests are billed to and processed under your own
                            Anthropic account rather than ours.
                        </p>
                    </div>
                    <p style={s.p}>
                        Where the assistant reasons over your calendar, it receives only the
                        event titles and times described in section 1 — never attendees,
                        locations, or descriptions.
                    </p>
                </Section>

                <Section id="security" title="5. How we protect your data">
                    <ul style={s.ul}>
                        <li>All traffic between your device and our servers is encrypted with HTTPS/TLS.</li>
                        <li>Passwords are stored only as cryptographic hashes.</li>
                        <li>Third-party credentials (calendar refresh tokens, delivery addresses, your AI key) are encrypted at rest with AES-GCM and bound to your user account, so a ciphertext cannot be replayed under a different user.</li>
                        <li>Every record is scoped to your organisation, and that scoping is enforced on every query rather than in the UI.</li>
                        <li>The database is not publicly reachable and is accessible only from the application.</li>
                    </ul>
                    <p style={s.p}>
                        No system is perfectly secure, and we cannot guarantee absolute security.
                    </p>
                </Section>

                <Section id="retention" title="6. Retention and deletion">
                    <p style={s.p}>
                        We keep your account data for as long as your account is active. Deleted
                        projects and tasks are soft-deleted first so they can be restored, then
                        removed. Cached calendar events are replaced on every sync and are
                        deleted outright when you disconnect the integration. Disconnecting an
                        integration also deletes the stored credentials for it.
                    </p>
                    <p style={s.p}>
                        You can delete your account yourself, in the app — Profile → Delete
                        Account in the web, iOS, and Android apps — or on our{' '}
                        <a href="/delete-account" style={s.link}>account deletion page</a>{' '}
                        if you no longer have the app installed. Deletion is scheduled rather
                        than immediate: it completes <strong>30 days</strong> after you confirm,
                        and until that date you can sign in as usual and cancel it. After that
                        we delete your account and its associated personal data, and it cannot
                        be undone.
                    </p>
                    <p style={s.p}>
                        Content you created inside an organisation's workspace belongs to that
                        workspace and remains with it after your account is deleted. If you own
                        a workspace that other members are still in, you will be asked to
                        transfer ownership or close the workspace before your account can be
                        deleted, so those members are not left without an owner.
                    </p>
                    <p style={s.p}>
                        If you cannot complete the deletion yourself, contact us at{' '}
                        <a href={`mailto:${LEGAL.contactEmail}`} style={s.link}>{LEGAL.contactEmail}</a>.
                    </p>
                </Section>

                <Section id="rights" title="7. Your rights">
                    <p style={s.p}>
                        Depending on where you live, you may have the right to access, correct,
                        export, or delete your personal data, to object to or restrict certain
                        processing, and to withdraw consent for optional features at any time.
                        You can exercise most of these directly in the app — profile settings for
                        your account details, and integration settings to disconnect a calendar
                        or notification channel. For anything else, contact us.
                    </p>
                    <p style={s.p}>
                        Deletion in particular is self-service and does not require contacting
                        us: Profile → Delete Account in the app, or our{' '}
                        <a href="/delete-account" style={s.link}>account deletion page</a> if you
                        no longer have it installed. Your account is deleted 30 days after you
                        confirm, and you can cancel at any point in that window by signing in.
                        Section 6 sets out what is removed and what stays with your workspace.
                    </p>
                    <p style={s.p}>
                        If your account was created by an organisation administrator, that
                        organisation controls the content in its workspace, and we act as a
                        processor on its behalf. Requests about that content may be directed to
                        the organisation.
                    </p>
                </Section>

                <Section id="children" title="8. Children">
                    <p style={s.p}>
                        The service is intended for workplace use and is not directed at children
                        under 13 (or under 16 where local law sets a higher age). We do not
                        knowingly collect their personal data.
                    </p>
                </Section>

                <Section id="transfers" title="9. International transfers">
                    <p style={s.p}>
                        Our infrastructure is hosted in the United States. If you access the
                        service from elsewhere, your data is transferred to and processed in the
                        United States, and may be processed in other countries where our service
                        providers operate.
                    </p>
                </Section>

                <Section id="changes" title="10. Changes to this policy">
                    <p style={s.p}>
                        We may update this policy as the product changes. When we do, we will
                        revise the effective date above, and for material changes we will give
                        notice in the app.
                    </p>
                </Section>

                <Section id="contact" title="11. Contact us">
                    <p style={s.p}>
                        Questions about this policy or your data:{' '}
                        <a href={`mailto:${LEGAL.contactEmail}`} style={s.link}>{LEGAL.contactEmail}</a>
                        <br />
                        {LEGAL.entity} — {LEGAL.jurisdiction}
                    </p>
                </Section>
            </main>

            <footer style={s.footer}>
                © {new Date().getFullYear()} {LEGAL.entity}. All rights reserved.
            </footer>
        </div>
    );
}
